import { Canvg } from 'canvg';
import kebabCase from 'lodash/kebabCase';
import { useState } from 'react';

import { analytics } from 'analytics';

import { getSankeyTitle } from '../../diagram/title';

const buildFilename = (form: SankeyForm) => {
  // Generate a kebab-case title from the form values
  const diagramTitle = kebabCase(getSankeyTitle(form.getValues()));
  // Generate yyyymmddhhmmss string
  const formattedTimestamp = (
    new Date().toISOString().replace(/T.+$/, '') +
    new Date().toTimeString().replace(/ .+$/, '')
  ).replace(/[:-]/g, '');
  // Build the complete filename
  return `${diagramTitle}-${formattedTimestamp}`;
};

const getSVGContent = () => {
  // Fetch the SVG canvas element
  const svgElement = document.getElementById('sankey_svg');
  if (!svgElement) {
    throw new Error('SVG element not found');
  }
  // Serialize the SVG element to a string and sanitize it
  const serializer = new XMLSerializer();
  const svgContent = serializer
    .serializeToString(svgElement)
    // Remove style attribute applied in SankeySVG component to avoid Canvg rendering issues.
    // The style is used to make the SVG container responsive in the web browser but is not needed for the PNG generation.
    .replace(/ style\=\".*\">/g, '>')
    // Replace all &nbsp; entities from the SVG content to avoid rendering issues (see https://github.com/sankeyart/sankeyart/issues/1160)
    .replaceAll('&nbsp;', ' ')
    // Add some line breaks to highlight where [g]roups start/end
    // and where each path/text/rect begins:
    .replace(/><(g|\/g|path|text|rect)/g, '>\r\n<$1');
  return {
    svgContent,
    width: Number(svgElement.getAttribute('width')),
    height: Number(svgElement.getAttribute('height')),
  };
};

const downloadFile = (filename: string, blob: Blob) => {
  // Generate a temporarily link to download the SVG and click it
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  // Clean up the link data
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getSVGBlob = () => {
  const { svgContent } = getSVGContent();
  return new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
};

const getPNGBlob = async (scale: number) => {
  const { svgContent, width, height } = getSVGContent();
  // Create offscreen canvas to render the PNG
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context not initialized');
  }
  // Render the SVG on the canvas and resize it to the desired scale
  const canvg = Canvg.fromString(ctx, svgContent);
  canvg.resize(width * scale, height * scale);
  await canvg.render();
  // Return the generated blob
  return await canvas.convertToBlob();
};

/**
 * Hook to handle the downloading of an image based on the provided form data.
 *
 * @param form - The form data containing the image download settings.
 * @param onClose - Callback function to be called when the download is complete.
 *
 * @returns An object containing:
 * - `isDownloading`: A boolean indicating if the download is in progress.
 * - `downloadImage`: A function to initiate the image download process.
 */
export const useImageDownloader = (form: SankeyForm, onClose: () => void) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadImageFormat = form.watch('download_image_format');
  const downloadScalePNG = Number(form.watch('download_scale_png') ?? 1);

  // Download the image asynchronously based on the selected format and
  // the selected scale and close the modal when done only if successful.
  const downloadImage = async () => {
    setIsDownloading(true);
    try {
      downloadFile(
        buildFilename(form),
        downloadImageFormat === 'png'
          ? await getPNGBlob(downloadScalePNG)
          : getSVGBlob()
      );
      onClose();
    } catch (error) {
      analytics.reportError('Something went wrong downloading the image', {
        error,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, downloadImage };
};
